import type { Paginated } from '@/shared/api/types';
import { asRec, isRec, listaDe, pick, toBool, toInstante, toNum, toStr } from './adaptHelpers';
import { adaptConfigWire } from './prefsWire';
import {
  alertaSchema,
  capacidadesSchema,
  notificacionSchema,
  sesionSchema,
  tokensSchema,
  usuarioSchema,
} from './schemas';
import {
  CAPACIDADES_BASICAS,
  type Alerta,
  type AlertaInput,
  type Capacidades,
  type Notificacion,
  type PreferenciasRemotas,
  type SesionActiva,
  type SesionAuth,
  type Tokens,
  type Usuario,
} from './types';

export { adaptConfigWire, toWirePreferencias } from './prefsWire';

/** Adaptadores del add-on de cuenta: usuarios, sesiones, preferencias, alertas, notificaciones y capacidades. */

const EXPIRA_POR_DEFECTO_S = 900;

export function adaptUsuario(raw: unknown): Usuario {
  const c = usuarioSchema.parse(raw);
  return {
    id: c.id,
    email: c.email,
    nombre: c.nombre,
    rol: c.rol,
    perfilTipo: c.perfil_tipo,
    regionPrincipal: c.region_principal,
    emailVerificado: c.email_verificado,
    creado: c.creado ?? '',
  };
}

export function adaptTokens(raw: unknown): Tokens {
  const c = tokensSchema.parse(raw);
  return {
    accessToken: c.access_token,
    refreshToken: c.refresh_token,
    expiresIn: c.expires_in ?? EXPIRA_POR_DEFECTO_S,
  };
}

/** Respuesta de login, registro y refresh: `{usuario, tokens}`. */
export function adaptSesionAuth(raw: unknown): SesionAuth {
  const rec = asRec(raw);
  return { usuario: adaptUsuario(pick(rec, 'usuario', 'user')), tokens: adaptTokens(rec.tokens) };
}

export function adaptSesiones(raw: unknown): SesionActiva[] {
  const lista = listaDe(raw, ['data', 'sesiones', 'items']);
  if (!lista) throw new Error('sesiones: la respuesta no trae una lista');
  return lista.flatMap((item): SesionActiva[] => {
    const r = sesionSchema.safeParse(item);
    if (!r.success) return [];
    const c = r.data;
    return [
      {
        id: c.id,
        creada: c.creada ?? '',
        ultimoUso: c.ultimo_uso ?? c.creada ?? '',
        userAgent: c.user_agent,
        actual: c.actual,
      },
    ];
  });
}

/* ───────────── Capacidades ───────────── */

const flag = (seccion: Record<string, unknown>, clave: string): boolean =>
  toBool(seccion[clave], false);

/** Respuesta de GET /v1/capacidades. Lanza si no parece del add-on (p. ej. un 200 con HTML del hosting). */
export function adaptCapacidades(raw: unknown): Capacidades {
  if (!isRec(raw) || !(isRec(raw.auth) || typeof raw.version === 'string'))
    throw new Error('capacidades: forma no reconocida');
  const c = capacidadesSchema.parse(raw);
  const limites = CAPACIDADES_BASICAS.limites;
  return {
    disponible: true,
    version: c.version,
    auth: {
      habilitada: flag(c.auth, 'habilitada'),
      registroAbierto: flag(c.auth, 'registro_abierto'),
      cookieMode: flag(c.auth, 'cookie_mode'),
      verificacionEmail: flag(c.auth, 'verificacion_email'),
      recuperacionPassword: flag(c.auth, 'recuperacion_password'),
    },
    push: {
      habilitado: flag(c.push, 'habilitado'),
      vapidPublicKey: toStr(c.push.vapid_public_key),
    },
    email: { habilitado: flag(c.email, 'habilitado') },
    mercado: {
      variaciones: flag(c.mercado, 'variaciones'),
      resumen: flag(c.mercado, 'resumen'),
      fuentesDetalle: flag(c.mercado, 'fuentes_detalle'),
    },
    admin: { fuentes: flag(c.admin, 'fuentes') },
    limites: {
      maxAlertas: toNum(c.limites.max_alertas) ?? limites.maxAlertas,
      maxFavoritos: toNum(c.limites.max_favoritos) ?? limites.maxFavoritos,
      maxSesiones: toNum(c.limites.max_sesiones) ?? limites.maxSesiones,
    },
  };
}

/* ───────────── Preferencias ───────────── */

/** `{version, config, actualizado}`; `config: null` y `version: 0` si nunca se guardó. */
export function adaptPreferencias(raw: unknown): PreferenciasRemotas {
  if (!isRec(raw)) throw new Error('preferencias: forma no reconocida');
  const config = adaptConfigWire(raw.config);
  if (raw.config !== null && raw.config !== undefined && config === null)
    throw new Error('preferencias: config no válida');
  return {
    version: Math.max(0, Math.trunc(toNum(raw.version) ?? 0)),
    config,
    actualizado: toInstante(raw.actualizado),
  };
}

/* ───────────── Alertas ───────────── */

export function adaptAlerta(raw: unknown, origen: Alerta['origen'] = 'servidor'): Alerta | null {
  const r = alertaSchema.safeParse(raw);
  if (!r.success) return null;
  const c = r.data;
  return {
    id: c.id,
    origen,
    nombre: c.nombre,
    activa: c.activa,
    objetivo: c.objetivo,
    filtro: c.objetivo === 'bcv' ? null : c.filtro,
    metrica: c.metrica,
    moneda: c.moneda,
    condicion: c.condicion,
    umbral: c.umbral,
    periodoDias: c.periodo_dias ?? 7,
    canales: c.canales.length > 0 ? c.canales : ['app'],
    cooldown: c.cooldown,
    estado: c.estado,
    creada: c.creada ?? '',
    ultimaEvaluacion: c.ultima_evaluacion,
    ultimaDisparada: c.ultima_disparada,
    ultimoValor: c.ultimo_valor,
  };
}

/** Cuerpo de POST/PATCH /alertas. Con una alerta parcial (PATCH) solo viajan las claves presentes. */
export function toWireAlerta(input: Partial<AlertaInput>): Record<string, unknown> {
  const wire: Record<string, unknown> = {};
  const copiar = (clave: keyof AlertaInput, destino: string): void => {
    if (input[clave] !== undefined) wire[destino] = input[clave];
  };
  copiar('nombre', 'nombre');
  copiar('activa', 'activa');
  copiar('objetivo', 'objetivo');
  copiar('metrica', 'metrica');
  copiar('moneda', 'moneda');
  copiar('condicion', 'condicion');
  copiar('umbral', 'umbral');
  copiar('periodoDias', 'periodo_dias');
  copiar('canales', 'canales');
  copiar('cooldown', 'cooldown');
  if (input.filtro !== undefined || input.objetivo === 'bcv') {
    const f = input.objetivo === 'bcv' ? null : input.filtro;
    wire.filtro = f && {
      rubro: f.rubro,
      categoria: f.categoria,
      presentacion: f.presentacion,
      region: f.region,
      fuente: f.fuente,
    };
  }
  return wire;
}

/* ───────────── Notificaciones y páginas ───────────── */

export function adaptNotificacion(
  raw: unknown,
  origen: Notificacion['origen'] = 'servidor',
): Notificacion | null {
  const r = notificacionSchema.safeParse(raw);
  if (!r.success) return null;
  const c = r.data;
  return {
    id: c.id,
    origen,
    tipo: c.tipo,
    titulo: c.titulo,
    cuerpo: c.cuerpo,
    datos: c.datos,
    enlace: c.enlace,
    creada: c.creada ?? '',
    leida: c.leida,
  };
}

/** `{data, total, page, limit, links}` con tolerancia: una lista suelta se trata como una única página. */
export function adaptPagina<T>(raw: unknown, adaptItem: (item: unknown) => T | null): Paginated<T> {
  const lista = listaDe(raw, ['data', 'items', 'resultados']);
  if (!lista) throw new Error('página: la respuesta no trae una lista');
  const items = lista.map(adaptItem).filter((v): v is T => v !== null);
  if (lista.length > 0 && items.length === 0) throw new Error('página: ningún elemento es válido');
  const rec = asRec(raw);
  const links = asRec(rec.links);
  return {
    data: items,
    total: Math.max(items.length, Math.trunc(toNum(rec.total) ?? items.length)),
    page: Math.max(1, Math.trunc(toNum(rec.page) ?? 1)),
    limit: Math.max(1, Math.trunc(toNum(rec.limit) ?? Math.max(items.length, 1))),
    links: { next: toStr(links.next), previous: toStr(links.previous) },
  };
}

export type NotificacionesPagina = Paginated<Notificacion> & { noLeidas: number };

export function adaptNotificacionesPagina(raw: unknown): NotificacionesPagina {
  const pagina = adaptPagina(raw, (item) => adaptNotificacion(item));
  const declaradas = toNum(asRec(raw).no_leidas);
  return { ...pagina, noLeidas: declaradas ?? pagina.data.filter((n) => !n.leida).length };
}

export function adaptAlertasPagina(raw: unknown): Paginated<Alerta> {
  return adaptPagina(raw, (item) => adaptAlerta(item));
}
